import { useState } from 'react'
import { ShieldAlert, Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { OgsmAccessField } from '@/components/custom/admin/ogsm-access-field'
import { UserAppAccessFields } from '@/components/custom/admin/user-app-access-fields'
import {
  UserSheet,
  UserSheetBody,
  UserSheetFieldLabel,
  UserSheetFooter,
} from '@/components/custom/admin/user-sheet'
import {
  sameAllowedApps,
  saveErrorMessage,
} from '@/features/admin/user-sheet-save'
import { showError, showSuccess } from '@/lib/toast'
import type { OgsmAccess } from '@/services/portal.service'
import type { AllowedApp } from '@/types/api'

const PORTCO_ROLES = [
  { value: 'PORTCO_STANDARD', label: 'Member' },
  { value: 'PORTCO_ADMIN', label: 'Admin' },
]

function getPortcoRoleLabel(role: string) {
  return PORTCO_ROLES.find((r) => r.value === role)?.label ?? role
}

interface PortcoUserLike {
  id: string
  name: string
  email: string
  role: string
  allowedApps: AllowedApp[]
}

interface EditPortcoUserSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: PortcoUserLike
  isCurrentUser?: boolean
  onChangeRole: (role: string) => Promise<unknown>
  onChangeAccess: (allowedApps: AllowedApp[]) => Promise<unknown>
  onRemove?: () => Promise<unknown>
  /** With `onChangeOgsmAccess`, shows the OGSM access field; without both, there is none. */
  ogsmAccess?: OgsmAccess
  onChangeOgsmAccess?: (ogsmAccess: OgsmAccess) => Promise<unknown>
}

export function EditPortcoUserSheet({
  open,
  onOpenChange,
  user,
  isCurrentUser = false,
  onChangeRole,
  onChangeAccess,
  onRemove,
  ogsmAccess: storedOgsmAccess,
  onChangeOgsmAccess,
}: EditPortcoUserSheetProps) {
  const [role, setRole] = useState(user.role)
  const [allowedApps, setAllowedApps] = useState<AllowedApp[]>(user.allowedApps)
  const [ogsmAccess, setOgsmAccess] = useState<OgsmAccess>(
    storedOgsmAccess ?? 'READ_ONLY'
  )
  const [syncedOgsmAccess, setSyncedOgsmAccess] = useState(storedOgsmAccess)
  // The stored level can arrive after the sheet opens (Greybull admin fetches it per company).
  if (storedOgsmAccess !== syncedOgsmAccess) {
    setSyncedOgsmAccess(storedOgsmAccess)
    setOgsmAccess(storedOgsmAccess ?? 'READ_ONLY')
  }
  const [roleError, setRoleError] = useState<string | null>(null)
  const [appsError, setAppsError] = useState<string | null>(null)
  const [ogsmError, setOgsmError] = useState<string | null>(null)
  const [pendingDemoteRole, setPendingDemoteRole] = useState<string | null>(
    null
  )
  const [removeConfirm, setRemoveConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)

  const roleChanged = role !== user.role
  const appsChanged = !sameAllowedApps(user.allowedApps, allowedApps)
  const showOgsmAccess =
    storedOgsmAccess !== undefined && onChangeOgsmAccess !== undefined
  // Admins always edit OGSM (backend rule); the draft level waits underneath.
  const ogsmLocked = role === 'PORTCO_ADMIN'
  const ogsmChanged =
    showOgsmAccess && !ogsmLocked && ogsmAccess !== storedOgsmAccess
  const isDirty = roleChanged || appsChanged || ogsmChanged

  const isSelfDemote =
    isCurrentUser && user.role === 'PORTCO_ADMIN' && role !== 'PORTCO_ADMIN'

  const changeRole = (next: string) => {
    setRole(next)
    setRoleError(null)
  }

  const changeAllowedApps = (next: AllowedApp[]) => {
    setAllowedApps(next)
    setAppsError(null)
  }

  const changeOgsmAccess = (next: OgsmAccess) => {
    setOgsmAccess(next)
    setOgsmError(null)
  }

  const applyChanges = async () => {
    setSaving(true)
    setRoleError(null)
    setAppsError(null)
    setOgsmError(null)
    let roleSaved = false
    let appsSaved = false
    try {
      if (roleChanged) {
        try {
          await onChangeRole(role)
          roleSaved = true
        } catch (error) {
          setRoleError(
            saveErrorMessage(error, "Couldn't save the role. Try again.")
          )
          return
        }
      }
      if (appsChanged) {
        try {
          await onChangeAccess(allowedApps)
          appsSaved = true
        } catch (error) {
          setAppsError(
            saveErrorMessage(
              error,
              roleSaved
                ? 'Role saved, but app access did not. Try again.'
                : "Couldn't save app access. Try again."
            )
          )
          return
        }
      }
      if (ogsmChanged && onChangeOgsmAccess) {
        try {
          await onChangeOgsmAccess(ogsmAccess)
        } catch (error) {
          setOgsmError(
            saveErrorMessage(
              error,
              roleSaved
                ? 'Role saved, but OGSM access did not. Try again.'
                : appsSaved
                  ? 'App access saved, but OGSM access did not. Try again.'
                  : "Couldn't save OGSM access. Try again."
            )
          )
          return
        }
      }
      showSuccess('Changes saved')
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => {
    if (!isDirty || saving || allowedApps.length === 0) return
    if (isSelfDemote) {
      setPendingDemoteRole(role)
      return
    }
    void applyChanges()
  }

  const handleRemove = async () => {
    if (!onRemove) return
    setRemoving(true)
    try {
      await onRemove()
      showSuccess(`${user.name} removed`)
      setRemoveConfirm(false)
      onOpenChange(false)
    } catch (error) {
      showError(error, 'Failed to remove user')
    } finally {
      setRemoving(false)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setRole(user.role)
      setAllowedApps(user.allowedApps)
      setOgsmAccess(storedOgsmAccess ?? 'READ_ONLY')
      setRoleError(null)
      setAppsError(null)
      setOgsmError(null)
    }
    onOpenChange(isOpen)
  }

  return (
    <>
      <UserSheet
        open={open}
        onOpenChange={handleOpenChange}
        title={
          <>
            {user.name}
            {isCurrentUser && (
              <span className="ml-2 align-[0.12em] font-sans text-[15px] font-normal text-primary/70">
                (you)
              </span>
            )}
          </>
        }
        subtitle={user.email}
      >
        <UserSheetBody>
          <div>
            <UserSheetFieldLabel htmlFor="edit-portco-user-role">
              Role
            </UserSheetFieldLabel>
            <Select value={role} onValueChange={changeRole} disabled={saving}>
              <SelectTrigger
                id="edit-portco-user-role"
                className="h-10 border-primary/20"
                aria-describedby={
                  roleError ? 'edit-portco-user-role-error' : undefined
                }
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PORTCO_ROLES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {roleError && (
              <Typography
                id="edit-portco-user-role-error"
                role="alert"
                variant="body-sm"
                className="mt-1.5 text-destructive"
              >
                {roleError}
              </Typography>
            )}
          </div>

          {showOgsmAccess && (
            <OgsmAccessField
              id="edit-portco-user-ogsm-access"
              value={ogsmAccess}
              onChange={changeOgsmAccess}
              locked={ogsmLocked}
              disabled={saving}
              error={ogsmError}
            />
          )}

          <div>
            <UserAppAccessFields
              allowedApps={allowedApps}
              onAllowedAppsChange={changeAllowedApps}
              initialAllowedApps={user.allowedApps}
              context="edit"
            />
            {appsError && (
              <Typography
                role="alert"
                variant="body-sm"
                className="mt-1.5 text-destructive"
              >
                {appsError}
              </Typography>
            )}
          </div>

          {onRemove && !isCurrentUser && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setRemoveConfirm(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 text-[12.5px] text-primary/70 transition-colors hover:text-destructive"
              >
                <Trash2 className="size-3.5" />
                Remove user from this company
              </button>
            </div>
          )}
        </UserSheetBody>

        <UserSheetFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || saving || allowedApps.length === 0}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </UserSheetFooter>
      </UserSheet>

      <AlertDialog
        open={pendingDemoteRole !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingDemoteRole(null)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <ShieldAlert className="size-3 text-destructive" />
              </div>
              Remove your admin access?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Changing your role to{' '}
              <span className="font-medium text-primary">
                {pendingDemoteRole ? getPortcoRoleLabel(pendingDemoteRole) : ''}
              </span>{' '}
              means you won&apos;t be able to manage the team anymore. Another
              admin will need to restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-xs">
              Keep Admin
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-8 bg-destructive text-xs text-white hover:bg-destructive/90"
              onClick={() => {
                setPendingDemoteRole(null)
                void applyChanges()
              }}
            >
              Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={removeConfirm}
        onOpenChange={(isOpen) => {
          if (!isOpen && !removing) setRemoveConfirm(false)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <Trash2 className="size-3 text-destructive" />
              </div>
              Remove team member?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-primary">{user.name}</span> (
              {user.email}) will lose access. They can be re-invited later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-xs" disabled={removing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-8 bg-destructive text-xs text-white hover:bg-destructive/90"
              disabled={removing}
              onClick={(e) => {
                e.preventDefault()
                void handleRemove()
              }}
            >
              {removing ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {saving && (
        <Typography variant="body-sm" className="sr-only">
          Saving changes…
        </Typography>
      )}
    </>
  )
}
